<template>
  <div>
    <v-row>
      <v-col cols="12" xl="6">
        <v-simple-table>
          <template v-slot:default>
            <tbody v-show="!$apollo.queries.result.loading">
              <tr>
                <th class="text-right">MSA Person ID:</th>
                <td>{{ result.personId }}</td>
              </tr>
              <tr>
                <th class="text-right">Name:</th>
                <td>{{ result.name }}</td>
              </tr>
              <tr>
                <th class="text-right">Given Name:</th>
                <td>{{ result.givenName }}</td>
              </tr>
              <tr>
                <th class="text-right">Family Name:</th>
                <td>{{ result.familyName }}</td>
              </tr>
              <tr>
                <th class="text-right">Email:</th>
                <td>{{ result.email }}</td>
              </tr>
              <tr>
                <th class="text-right">Phone Number:</th>
                <td>{{ result.phoneNumber }}</td>
              </tr>

              <tr v-for="(rp, i) of result.registrations" :key="rp._id">
                <th v-if="!i" :rowspan="result.registrations.length" class="text-right">Members</th>
                <td>{{ getMemberLabel(rp.registration) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>

    <v-stepper
      v-model="step"
      class="mt-5"
    >
      <v-stepper-header>
        <v-stepper-step
          :complete="step > 1"
          step="1"
        >
          Selection
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step
          :complete="step > 2"
          step="2"
        >
          Confirmation
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content step="1">
          <v-form>
            <v-row>
              <v-col cols="12" md="4">
                <v-card outlined>
                  <v-card-text>
                    <p class="text-overline">Matching Strategies:</p>
                    <v-checkbox
                      dense
                      label="Phone Number"
                      v-model="includeStrategies.PHONE_NUMBER_EXACT"
                    />
                    <v-checkbox
                      dense
                      label="Email Address"
                      v-model="includeStrategies.EMAIL_EXACT"
                    />
                    <v-checkbox
                      dense
                      label="Email (Fuzzy)"
                      v-model="includeStrategies.EMAIL_FUZZY"
                    />
                    <v-checkbox
                      dense
                      label="Name"
                      v-model="includeStrategies.NAME_EXACT"
                    />
                    <v-checkbox
                      dense
                      label="Name (Fuzzy)"
                      v-model="includeStrategies.NAME_FUZZY"
                    />
                    <v-select
                      dense
                      label="Mode"
                      :items="includeModeOptions"
                      v-model="includeMode"
                    />
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card outlined>
                  <v-card-text>
                    <p class="text-overline">Options:</p>
                    <v-switch
                      dense
                      label="Restrict to members of same type"
                      v-model="restrictToMemberOfSameType"
                    />
                    <v-combobox
                      v-show="!restrictToMemberOfSameType"
                      v-model="memberTypes"
                      :items="allMemberTypes"
                      :loading="$apollo.queries.allMemberTypes.loading"
                      label="Member Types"
                      item-text="name"
                      item-value="value"
                      placeholder="All"
                      multiple
                      chips
                      clearable
                    ></v-combobox>
                    <v-switch
                      dense
                      label="Restrict to members of similar name"
                      v-model="restrictToMemberOfSimilarName"
                    />
                    <v-switch
                      dense
                      label="Restrict to members of same ABN"
                      v-model="restrictToMemberOfSameAbn"
                    />
                    <v-slider
                      dense
                      v-show="restrictToMemberOfSimilarName"
                      label="Threshold"
                      v-model="memberNameFuzzyThreshold"
                      :step="0.05"
                      :max="1"
                      :min="0.1"
                    />
                    <v-combobox
                      dense
                      multiple
                      chips
                      clearable
                      class="mt-5"
                      v-model="licenseStatuses"
                      :items="licenseStatusOptions"
                      label="License Statuses"
                      placeholder="All"
                    ></v-combobox>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card outlined>
                  <v-card-text>
                    <p class="text-overline">Refine Results:</p>
                    <v-switch
                      dense
                      label="Hide Inactive People"
                      v-model="hideInactivePeople"
                    />
                    <v-switch
                      dense
                      label="Hide Non-User People (Trainees, Trainers, etc)"
                      v-model="hideNonUsers"
                    />
                    <v-text-field
                      dense
                      clearable
                      label="Search"
                      v-model="search"
                    />
                    <v-slider
                      dense
                      v-show="!!this.search"
                      label="Threshold"
                      v-model="searchThreshold"
                      :step="0.05"
                      :max="1"
                      :min="0.1"
                    />
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-form>

          <v-row>
            <v-col>
              <v-data-table
                dense
                hide-default-footer
                show-select
                v-model="selectedItems"
                :loading="$apollo.queries.result.loading"
                :headers="selectionListHeaders"
                :items="filteredResults"
                :items-per-page="-1"
                item-key="_id">
                <template v-slot:[`item.targetPerson.name`]="{ item, value }">
                  <td :class="{
                    'font-italic': item.strategies[strategies.NAME_FUZZY],
                    'font-weight-bold': item.strategies[strategies.NAME_EXACT] || item.strategies[strategies.NAME_FUZZY]
                    }">
                    {{ value }}
                  </td>
                </template>
                <template v-slot:[`item.targetPerson.email`]="{ item, value }">
                  <td :class="{
                    'font-italic': item.strategies[strategies.EMAIL_FUZZY],
                    'font-weight-bold': item.strategies[strategies.EMAIL_EXACT] || item.strategies[strategies.EMAIL_FUZZY]
                    }">
                    {{ value }}
                  </td>
                </template>
                <template v-slot:[`item.targetPerson.phoneNumber`]="{ item, value }">
                  <td :class="{ 'font-weight-bold': item.strategies[strategies.PHONE_NUMBER_EXACT] }">
                    {{ value }}
                  </td>
                </template>
                <template v-slot:[`item.targetPerson.hasIdentity`]="{ value }">
                  <td>{{ value | yesNo }}</td>
                </template>
                <template v-slot:[`item.targetPerson.lastLoggedInAt`]="{ value }">
                  <td>{{ value | formatDate }}</td>
                </template>
                <template v-slot:[`item.targetPerson.registrations`]="{ item }">
                  <td>
                    <p v-for="rp in item.targetPerson.registrations" :key="rp._id">
                      <template v-if="rp.isActive">
                        {{ getMemberLabel(rp.registration) }}
                        <template v-if="rp.isAdminForRegistration">
                          &star;
                        </template>
                      </template>
                    </p>
                  </td>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-btn
                color="primary"
                text
                :disabled="selectedItems.length < 1"
                @click="step++"
              >
                <v-icon>preview</v-icon>
                Next
              </v-btn>
            </v-col>
            <v-col cols="6" class="text-right">
              <v-btn
                text
                @click="resetSelection"
              >
                Clear
              </v-btn>
            </v-col>
          </v-row>
        </v-stepper-content>

        <v-stepper-content step="2">
          <v-row>
            <v-col v-show="selectedItems.length > 0">
              <p class="mt-5">
                By performing this action, the following target people will be consolidated to the source person. All consolidated accounts will be deactivated and unlinked from their linked members.
              </p>
              <v-data-table
                dense
                hide-default-footer
                :headers="confirmListHeaders"
                :items="selectedItems"
                :items-per-page="-1"
                item-key="_id">
                <template v-slot:[`item.targetPerson.lastLoggedInAt`]="{ value }">
                  <td>{{ value | formatDate }}</td>
                </template>
              </v-data-table>
              <p class="mt-5">
                The following items will be transferred from the consolidated persons to the target person.
              </p>
              <ul class="mb-5">
                <li>Linkages and role assignments to MSA Members</li>
                <li>Training records</li>
              </ul>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-btn
                color="danger"
                text
                :loading="consolidating"
                :disabled="consolidating"
                @click="onConsolidate"
              >
                <v-icon>merge</v-icon>
                Consolidate
              </v-btn>
            </v-col>
            <v-col cols="6" class="text-right">
              <v-btn
                text
                @click="onCancel"
              >
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { capitalCase } from 'change-case'
import { uniq, intersection } from 'lodash'
import Fuse from 'fuse.js'
import { LicenseStatus } from '@mymsa/common'

export default {
  name: 'PersonDuplicateList',

  props: {
    personId: {
      type: String,
      required: true
    }
  },

  data: () => {
    const baseHeaders = [
      {
        text: 'MSA Person ID',
        value: 'targetPerson.personId'
      },
      {
        text: 'Name',
        value: 'targetPerson.name'
      },
      {
        text: 'Email',
        value: 'targetPerson.email'
      },
      {
        text: 'Phone Number',
        value: 'targetPerson.phoneNumber'
      },
      {
        text: 'Has Login',
        value: 'targetPerson.hasIdentity'
      },
      {
        text: 'Last Log In',
        value: 'targetPerson.lastLoggedInAt'
      }
    ]
    return {
      step: 1,
      selectionListHeaders: [
        ...baseHeaders,
        {
          text: 'Members',
          value: 'targetPerson.registrations'
        },
        {
          text: 'Actions',
          value: 'actions',
          class: 'text-end',
          sortable: false
        }
      ],
      confirmListHeaders: [
        ...baseHeaders
      ],
      result: {
        personId: null,
        name: '',
        givenName: '',
        familyName: '',
        email: '',
        phoneNumber: '',
        registrations: [],
        matchedPersons: []
      },
      strategies: {
        PHONE_NUMBER_EXACT: 'PHONE_NUMBER_EXACT',
        EMAIL_EXACT: 'EMAIL_EXACT',
        NAME_EXACT: 'NAME_EXACT',
        NAME_FUZZY: 'NAME_FUZZY',
        EMAIL_FUZZY: 'EMAIL_FUZZY'
      },
      includeStrategies: {
        PHONE_NUMBER_EXACT: true,
        EMAIL_EXACT: true,
        NAME_EXACT: true,
        NAME_FUZZY: true,
        EMAIL_FUZZY: true
      },
      includeModeOptions: [
        { value: 'OR', text: 'OR' },
        { value: 'AND', text: 'AND' }
      ],
      includeMode: 'OR',
      selectedItems: [],
      restrictToMemberOfSameType: true,
      restrictToMemberOfSimilarName: false,
      restrictToMemberOfSameAbn: false,
      memberNameFuzzyThreshold: 0.8,
      licenseStatusOptions: Object.entries(LicenseStatus).map(([key, value]) => ({
        text: capitalCase(value),
        value
      })),
      licenseStatuses: [],
      search: '',
      searchThreshold: 0.6,
      hideInactivePeople: true,
      hideNonUsers: false,
      allMemberTypes: [],
      memberTypes: [],
      consolidating: false
    }
  },

  computed: {
    enabledStrategies () {
      return Object.keys(this.includeStrategies)
        .filter(key => this.includeStrategies[key])
    },

    searchedResults () {
      let results = this.result.matchedPersons

      if (this.search) {
        const fuseOptions = {
          keys: [
            'targetPerson.name',
            'targetPerson.email',
            'targetPerson.phoneNumber',
            'targetPerson.registrations.registration.memberName'
          ],
          threshold: this.searchThreshold
        }
        const fuse = new Fuse(results, fuseOptions)
        results = fuse.search(this.search).map(o => o.item)
      }

      return results
    },

    filteredResults () {
      let results = this.searchedResults
      const enabledStrategies = this.enabledStrategies
      const licenseStatuses = this.licenseStatuses.map(o => o.value)
      const memberTypes = this.memberTypes.map(o => o.value)

      // Hide resolved entries
      results = results.filter(result => !result.isResolved)

      if (this.hideInactivePeople) {
        results = results
          .filter(result => result.targetPerson.isActive) // Exclude people that aren't active
          .filter(result => result.targetPerson.registrations.some(rp => rp.isActive)) // Exclude people that don't have at least one active linkage to a member
      }

      if (this.hideNonUsers) {
        results = results.filter(result => result.targetPerson.hasIdentity)
      }

      if (this.includeMode === 'OR') {
        results = results.filter(result => enabledStrategies.some(key => result.strategies[key]))
      } else {
        results = results.filter(result => enabledStrategies.every(key => result.strategies[key]))
      }

      if (this.restrictToMemberOfSameType) {
        results = results.filter(result => {
          const targetMemberTypes = result.targetPerson.registrations.flatMap(rp => rp.registration?.activities?.map(a => a.type))
          const intersect = intersection(targetMemberTypes, this.personMemberTypes)
          return intersect.length > 0
        })
      } else if (this.memberTypes.length) {
        results = results.filter(result => {
          const targetMemberTypes = result.targetPerson.registrations.flatMap(rp => rp.registration?.activities?.map(a => a.type))
          const intersect = intersection(targetMemberTypes, memberTypes)
          intersect.length > 0 && console.log({ targetMemberTypes, memberTypes, intersect })
          return intersect.length > 0
        })
      }

      if (this.restrictToMemberOfSameAbn) {
        const sourceAbns = this.result.registrations.flatMap(rp => rp.registration?.abn)
        results = results.filter(result => result.targetPerson.registrations.some(rp => sourceAbns.includes(rp.registration.abn)))
      }

      if (licenseStatuses.length) {
        results = results.filter(result => {
          const targetLicenseStatuses = result.targetPerson.registrations.flatMap(rp => rp.registration?.activities?.map(a => a.licenseStatus))
          const intersect = intersection(targetLicenseStatuses, licenseStatuses)
          console.log({ targetLicenseStatuses, licenseStatuses, intersect })
          return intersect.length > 0
        })
      }

      if (this.restrictToMemberOfSimilarName) {
        const fuseOptions = {
          keys: [
            'targetPerson.registrations.registration.memberName'
          ],
          threshold: this.memberNameFuzzyThreshold
        }
        const personMemberNames = this.personMemberNames
        const fuse = new Fuse(results, fuseOptions)
        const searchResults = personMemberNames.flatMap(name => fuse.search(name))
        results = searchResults.map(o => o.item)
      }

      return results
    },

    personMemberTypes () {
      return uniq(this.result.registrations.flatMap(rp => rp.registration?.activities?.map(a => a.type)))
    },

    personMemberNames () {
      return uniq(this.result.registrations.map(rp => rp.registration?.memberName))
    }
  },

  apollo: {
    result: {
      query: gql`query PersonDuplicateList ($personId: MongoID!) {
        result: personById(_id: $personId) {
          _id
          personId
          name
          givenName
          familyName
          email
          phoneNumber
          registrations {
            _id
            isActive
            isPrimaryContact
            isAdminForRegistration
            registration {
              _id
              memberNumber
              memberName
              abn
              primaryActivityId
              activities {
                _id
                type
                licenseStatus
              }
            }
          }
          matchedPersons {
            _id
            isResolved
            strategies {
              PHONE_NUMBER_EXACT
              EMAIL_EXACT
              NAME_EXACT
              NAME_FUZZY
              EMAIL_FUZZY
            }
            targetPerson {
              _id
              personId
              isActive
              name
              givenName
              familyName
              email
              phoneNumber
              hasIdentity
              lastLoggedInAt
              registrations {
                _id
                isActive
                isPrimaryContact
                isAdminForRegistration
                registration {
                  _id
                  memberNumber
                  memberName
                  abn
                  primaryActivityId
                  activities {
                    _id
                    type
                    licenseStatus
                  }
                }
              }
            }
          }
        }
      }`,
      variables () {
        return {
          personId: this.personId
        }
      },
      error (e) { this.onError(e) }
    },

    allMemberTypes: {
      query: gql`query AllMemberTypes {
        allMemberTypes: memberTypeMany {
          _id
          name
          value
        }
      }`,
      error (e) { this.onError(e) }
    }
  },

  methods: {
    resetSelection () {
      this.selectedItems = []
    },

    onError (e) {
      this.$emit('error', e)
    },

    getMemberLabel (registration) {
      const memberNumber = registration.memberNumber || 'Non-Member'

      if (registration.activities.length === 0) {
        return `${memberNumber} (No License)`
      }

      const primaryActivtity = registration.primaryActivityId
        ? registration.activities.find(o => o._id === registration.primaryActivityId)
        : registration.activities[0]

      return `${memberNumber}
        - ${registration.abn || 'No ABN'}
        - ${registration.memberName} (${capitalCase(primaryActivtity.type)}
        - ${capitalCase(primaryActivtity.licenseStatus || 'No License')})`
    },

    async onConsolidate () {
      this.consolidating = true
      const ids = this.selectedItems.map(o => o._id)

      try {
        for (const id of ids) {
          await this.$apollo.mutate({
            mutation: gql`mutation ($id: MongoID!) {
              matchedPersonMerge(_id: $id) {
                _id
                isResolved
                resolvedAt
              }
            }`,
            variables: {
              id
            }
          })
          // Remove item from selectedItems after success incase another item errors, as it has already been processed.
          this.selectedItems.splice(this.selectedItems.findIndex(o => o._id === id), 1)
        }
      } catch (e) {
        this.onError(e)
        return
      } finally {
        this.consolidating = false
      }
      this.resetSelection()
      this.$apollo.queries.result.refresh()
      this.$emit('info', `Consolidated ${ids.length} people.`)
      this.$emit('close')
    },

    onCancel () {
      this.resetSelection()
      this.$emit('close')
    }
  },

  watch: {
    // Reset selected items when results change.
    'result.items' () {
      this.resetSelection()
    },

    personId () {
      // Reset result when personId changes to prevent showing stale data.
      this.result = {
        registrations: [],
        matchedPersons: []
      }
    }
  }
}
</script>
